import type { AnyAction } from "@reduxjs/toolkit";
import type { ToastCTAProps } from "@v1/components/designSystem/alert/Toast";
import type { ErrorDetails } from "schemas/errors";

import * as React from "react";

import { createSlice } from "@reduxjs/toolkit";
import { Button } from "@replo/design-system/components/button/Button";
import toast from "@v1/components/designSystem/alert/Toast";
import { ConnectShopifyIntegrationCta } from "@v2/components/ConnectShopifyCallout";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { analytics } from "@v2/features/infra/analytics/tracking";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { docs } from "@v2/utils/docs";
import { SHOPIFY_APP_LISTING_URL } from "@v2/utils/env";
import { getErrorDetails } from "@v2/utils/get-error-details";
import { routes } from "@v2/utils/router";
import { generatePath, Link } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

function ToastConnectShopifyCta() {
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();

  // Note (Noah, 2026-01-06): This should never happen, but in the edge case where for
  // some reason we don't have a project id/workspace id, we just direct to the app store
  // without setting a cookie. User will have the choice to connect the app to the right
  // project after they install it, through our install flow
  if (!projectId || !workspaceId) {
    return (
      <Button
        variant="primary"
        size="base"
        render={
          <a href={SHOPIFY_APP_LISTING_URL} target="_blank" rel="noreferrer" />
        }
      >
        Install Shopify App
      </Button>
    );
  }

  return (
    <ConnectShopifyIntegrationCta
      cookieValue={{
        type: "noShopifyErrorModal",
        projectId,
        workspaceId,
      }}
    />
  );
}

function NavigateButton({
  location,
  title,
}: {
  title: string;
  location: (opts: {
    projectId: string | undefined;
    workspaceId: string | undefined;
  }) => string;
}) {
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const to = location({ projectId, workspaceId: workspaceId ?? undefined });
  return (
    <Button variant="primary" size="base" render={<Link to={to} />}>
      {title}
    </Button>
  );
}

const getToastCtaForErrorDetails = (
  errorDetails: ErrorDetails,
): ToastCTAProps | null => {
  if (!errorDetails.callToAction) {
    return null;
  }
  return exhaustiveSwitch(errorDetails.callToAction)({
    installApp: (): ToastCTAProps => ({
      cta: <ToastConnectShopifyCta />,
    }),
    callback: (data) => ({
      cta: (
        <Button variant="primary" size="base" onClick={data.onClick}>
          {data.name}
        </Button>
      ),
    }),
    link: (data) => ({
      cta: data.name,
      ctaHref: data.to,
      ctaTarget: data.target,
    }),
    closeModal: () => null,
    navigate: (data) => {
      return exhaustiveSwitch(data.location)({
        integrationHub: (): ToastCTAProps => ({
          cta: (
            <NavigateButton
              title="View Integrations"
              location={({ projectId }) => {
                return generatePath(
                  routes.shops.settings.shops.integrations.home,
                  {
                    projectId,
                  },
                );
              }}
            />
          ),
        }),
        home: () => ({
          cta: "Go Home",
          ctaHref: routes.home.root,
        }),
        workspace: ({ workspaceName }) => ({
          cta: (
            <NavigateButton
              title={`Go to "${workspaceName}"`}
              location={({ workspaceId }) => {
                return generatePath(routes.workspace.settings.general, {
                  workspaceId,
                });
              }}
            />
          ),
        }),
      });
    },
    custom: (data): ToastCTAProps | null => {
      if (!data.callToActionType) {
        return null;
      }
      return exhaustiveSwitch(data.callToActionType)({
        closeModal: () => null,
        navigateToLink: (data): ToastCTAProps => {
          return exhaustiveSwitch(data.link)({
            "errors.chunkingError": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.chunkingError,
            }),
            "errors.wrongJsonExtension": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.wrongJsonExtension,
            }),
            "errors.indexBackupError": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.indexBackupError,
            }),
            "errors.shopifyIntegrationAlreadyAssigned": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.shopifyIntegrationAlreadyAssigned,
            }),
            "errors.shopifyIntegrationMissingAccessScopes": (data) => ({
              cta: "Authorize",
              ctaHref: data.url,
            }),
            "errors.projectConnectedToBillingIntegrationForShopifyWorkspace":
              () => ({
                cta: "Learn more",
                ctaHref:
                  docs.errors
                    .projectConnectedToBillingIntegrationForShopifyWorkspace,
              }),
            "errors.themeAssetSizeLimitExceeded": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.themeAssetSizeLimitExceeded,
            }),
            "errors.themeTotalSizeLimitExceeded": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.themeTotalSizeLimitExceeded,
            }),
            "errors.pageMetafieldDefinitionsChanged": () => ({
              cta: "Learn more",
              ctaHref: docs.errors.pageMetafieldDefinitionsChanged,
            }),
            "settings.billing": () => ({
              cta: "Upgrade Plan",
              ctaHref: "/billing",
            }),
            customLink: (data) => ({
              cta: "Learn more",
              ctaHref: data.url,
            }),
          });
        },
        reload: () => ({
          cta: "Refresh",
          ctaOnClick: () => window.location.reload(),
        }),
        installApp: (): ToastCTAProps => ({
          cta: <ToastConnectShopifyCta />,
        }),
        goToHomepage: () => ({
          cta: "Go Home",
          ctaHref: routes.home.root,
        }),
        navigateToProjectEditor: () => ({
          cta: (
            <NavigateButton
              title="Go back to project"
              location={({ projectId }) => {
                return generatePath(routes.editor.project, { projectId });
              }}
            />
          ),
        }),
      });
    },
  });
};

/**
 * A slice for any errors and exceptions
 */
const errorSlice = createSlice({
  name: "error",
  initialState: {},
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* If you define a next, automatically redirect after an API call is successful */
      .addMatcher(
        (action: AnyAction) => {
          return (
            Boolean(action.type?.endsWith("_ERROR")) ||
            Boolean(action.type?.endsWith("/rejected"))
          );
        },
        (_state, action) => {
          const details = getErrorDetails({
            type: "rsaaOrRtkQueryAction",
            action,
          });

          if (details && details.type === "toast") {
            const cta = getToastCtaForErrorDetails(details.errorDetails);
            if (cta) {
              toast({
                type: "error",
                ...details.errorDetails,
                ...cta,
              });
            } else {
              toast({
                type: "error",
                ...details.errorDetails,
              });
            }

            if (details.errorKey === "unknown") {
              analytics.logEvent("error.unknown.shown", {
                errorId: undefined,
                endpoint: details.endpointName,
                errorSource: "rtkQuery",
              });
            }
          }
        },
      );
  },
});

const { reducer } = errorSlice;

export default reducer;
